import assessmentsJson from "./data/preliminary-assessments.json";
import claimsJson from "./data/claims.json";
import edgesJson from "./data/narrator-edges.json";
import eventsJson from "./data/events.json";
import narratorsJson from "./data/narrator-registry.json";
import type {
  AssessmentRecord,
  ClaimRecord,
  EventRecord,
  GraphLink,
  GraphMode,
  GraphNode,
  NarratorEdge,
  NarratorRecord,
} from "./types";

export const events = eventsJson as EventRecord[];
export const claims = claimsJson as ClaimRecord[];
export const assessments = assessmentsJson as AssessmentRecord[];
export const narrators = narratorsJson as NarratorRecord[];
export const narratorEdges = edgesJson as NarratorEdge[];

const tierColor: Record<number, string> = {
  1: "#c6a86a",
  2: "#79a7b9",
  3: "#7f8790",
  4: "#a45d58",
};

const visibleIds = new Set(
  narrators
    .filter((person) => person.importance_tier <= 2)
    .map((person) => person.id),
);

for (const id of [
  "person.al-tabari",
  "person.al-baladhuri",
  "person.hisham-al-kalbi",
  "person.sulayman-ibn-abi-rashid",
  "person.khalid-ibn-yazid-al-qasri",
  "person.ahmad-ibn-janab",
  "person.zakariyya-ibn-yahya",
]) {
  visibleIds.add(id);
}

export const graphNodes: GraphNode[] = narrators
  .filter((person) => visibleIds.has(person.id))
  .map((person) => ({
    ...person,
    color: tierColor[person.importance_tier],
    val: person.importance_tier === 1 ? 7 : person.importance_tier === 2 ? 4 : 2.5,
  }));

const transmissionLinks: GraphLink[] = narratorEdges
  .filter((edge) => visibleIds.has(edge.from) && visibleIds.has(edge.to))
  .map((edge) => ({
    source: edge.from,
    target: edge.to,
    type: "transmission",
    rationale: edge.rationale,
    certainty: edge.certainty,
  }));

const convergenceLinks: GraphLink[] = [
  ["person.muhammad-al-baqir", "person.abu-mikhnaf", "Broad refusal, Medina–Mecca movement, and Kufan contact converge across independently developing recensions."],
  ["person.husayn-ibn-abd-al-rahman", "person.abu-mikhnaf", "Two early Kufan recensions independently preserve al-Hurr's bare defection."],
  ["person.husayn-ibn-abd-al-rahman", "person.muhammad-al-baqir", "The recensions share parts of the broad event arc but not every microreport."],
].map(([source, target, rationale]) => ({
  source,
  target,
  type: "convergence" as const,
  rationale,
  certainty: "recension-level comparison",
}));

const contradictionLinks: GraphLink[] = [
  ["person.muhammad-al-baqir", "person.abu-mikhnaf", "Al-Hurr appears as a brief warning figure in one recension and a vanguard commander in the other."],
  ["person.awana-ibn-al-hakam", "person.abu-mikhnaf", "Interested counter-traditions distribute responsibility for the aftermath differently."],
  ["person.husayn-ibn-abd-al-rahman", "person.abu-mikhnaf", "The accounts materially vary on Muslim ibn Aqil's final fight and the placement of the cane motif."],
].map(([source, target, rationale]) => ({
  source,
  target,
  type: "contradiction" as const,
  rationale,
  certainty: "variant comparison",
}));

const linksByMode: Record<GraphMode, GraphLink[]> = {
  transmission: transmissionLinks,
  convergence: convergenceLinks,
  contradiction: contradictionLinks,
};

export const graphDataForMode = (mode: GraphMode) => ({
  nodes: graphNodes,
  links: linksByMode[mode],
});

export const claimsForEvent = (eventId: string) => claims.filter((claim) => claim.event_id === eventId);

export const assessmentForClaim = (claimId: string) =>
  assessments.find((assessment) => assessment.claim_id === claimId);

export const defaultNarrator = narrators.find((person) => person.id === "person.abu-mikhnaf") ?? narrators[0];
